import { gql, useLazyQuery } from '@apollo/client';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import SlideoutMenu from '@orn/shared-web/components/SlideoutMenu';
import { CalendarIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { PATHS } from '../../../paths';
import { useAuthState } from '../../../providers/AuthProvider';
import { useNavigateWithDateRange } from '../../../providers/DateRangeProvider';
import { formatCalendarDate, toCurrency } from '../../../utils/formatting';
import Entity from '../../entity';
import { SlideoutAmountDisplay, SlideoutEntityHeader, SlideoutHeaderCaption, SlideoutTimingTag } from '../header';
import {
  AccountRow,
  AttachmentRow,
  IsRecurringRow,
  JsonDebugRow,
  LastPaymentRow,
  NameRow,
  NotesRow,
  RecurringFrequencyRow,
  RecurringTypeRow,
  YearlySpendRow,
} from '../rows';
import TransactionSlideout from '../TransactionSlideout';

import type {
  BillSlideoutDetailsFragment,
  GetBillDetailsQuery,
  GetBillDetailsQueryVariables,
} from './__generated__/BillSlideout.gql';

const BillSlideoutDetailsFragmentDocument = gql`
  fragment BillSlideoutDetails on Bill {
    id
    status
    source
    senderIdentifier
    createdAt
    providerName
    amountDue
    dueDate
    description
    merchant {
      id
      ...EntityAvatar
    }
    documents {
      ...AttachmentRowDocument
    }
    billSubmission {
      id
      rawJson
    }
    transactions {
      id
      financialAccount {
        id
        name
        mask
        subtype
        ...EntityAvatar
      }
    }
    recurringTransaction {
      id
      type
      description
      frequency
      nextOccurrence
      amountRange
      rawJson
      lastTransaction {
        id
        date
        amount
        financialAccount {
          id
          name
          mask
          ...EntityAvatar
        }
      }
    }
  }
  ${Entity.Avatar.Fragment}
  ${AttachmentRow.Fragment}
`;

const GET_BILL_DETAILS = gql`
  query GetBillDetails($id: ID!) {
    getBill(id: $id) {
      ...BillSlideoutDetails
    }
  }
  ${BillSlideoutDetailsFragmentDocument}
`;

export type BillDetails = BillSlideoutDetailsFragment;

interface IBaseBillSlideoutProps {
  bill: BillDetails | null;
  isOpen: boolean;
  isAdmin?: boolean;
  onClickFinancialAccount: () => void;
  onClickMerchant?: () => void;
  onNavigateToTransaction: (transactionId: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
}

export const BaseBillSlideout = ({
  bill,
  isOpen,
  isAdmin = false,
  onClickFinancialAccount,
  onClickMerchant,
  onNavigateToTransaction,
  onOpenChange,
}: IBaseBillSlideoutProps) => {
  if (!isOpen) {
    return null;
  }

  return (
    <SlideoutMenu isOpen={isOpen} onOpenChange={onOpenChange} ariaLabel="Bill details">
      {({ close }) => {
        if (!bill) {
          return (
            <SlideoutMenu.Content>
              <div className="py-50 flex items-center justify-center">
                <LoadingIndicator size="lg" />
              </div>
            </SlideoutMenu.Content>
          );
        }

        const displayName = bill.merchant?.name ?? bill.providerName;
        const primaryTransaction = bill.transactions[0] ?? null;
        const recurringTransaction = bill.recurringTransaction;
        const hasDocuments = bill.documents.length > 0;
        const amountNumber = Number(bill.amountDue);
        const formattedAmount = toCurrency(Math.abs(amountNumber));

        return (
          <>
            <SlideoutMenu.Header
              onClose={close}
              leftContent={<SlideoutTimingTag date={bill.dueDate} status={bill.status} />}
            >
              <div className="flex w-full flex-col items-center gap-4">
                <SlideoutEntityHeader entity={bill.merchant ?? null} name={displayName} onClick={onClickMerchant} />
                <div className="flex w-full flex-col items-center justify-center">
                  <SlideoutAmountDisplay value={formattedAmount} />
                  <SlideoutHeaderCaption icon={<CalendarIcon className="size-4.5 text-utility-brand-300 shrink-0" />}>
                    {`Due ${formatCalendarDate(bill.dueDate, 'medium')}`}
                  </SlideoutHeaderCaption>
                </div>
              </div>
            </SlideoutMenu.Header>
            <SlideoutMenu.Content>
              <div className="flex flex-col gap-3">
                {hasDocuments && (
                  <SlideoutMenu.Container className="overflow-y-auto">
                    <AttachmentRow bill={bill} />
                    <JsonDebugRow
                      isAdmin={isAdmin}
                      label="Bill submission JSON"
                      rawJson={bill.billSubmission?.rawJson}
                    />
                  </SlideoutMenu.Container>
                )}
                {primaryTransaction && primaryTransaction.financialAccount && (
                  <SlideoutMenu.Container className="overflow-y-auto">
                    <AccountRow
                      financialAccount={primaryTransaction.financialAccount}
                      onClick={onClickFinancialAccount}
                    />
                  </SlideoutMenu.Container>
                )}
                {recurringTransaction && (
                  <SlideoutMenu.Container className="overflow-y-auto">
                    <IsRecurringRow />
                    <NameRow name={recurringTransaction.description} />
                    <RecurringTypeRow type={recurringTransaction.type} amountRange={recurringTransaction.amountRange} />
                    <RecurringFrequencyRow frequency={recurringTransaction.frequency} />
                    <LastPaymentRow
                      lastTransaction={
                        recurringTransaction.lastTransaction?.financialAccount
                          ? {
                              ...recurringTransaction.lastTransaction,
                              financialAccount: recurringTransaction.lastTransaction.financialAccount,
                            }
                          : null
                      }
                      onClick={onNavigateToTransaction}
                    />
                    <YearlySpendRow
                      amountRange={recurringTransaction.amountRange}
                      frequency={recurringTransaction.frequency}
                    />
                    <JsonDebugRow
                      isAdmin={isAdmin}
                      label="Recurring transaction JSON"
                      rawJson={recurringTransaction.rawJson}
                    />
                  </SlideoutMenu.Container>
                )}
                <NotesRow description={bill.description} />
              </div>
            </SlideoutMenu.Content>
          </>
        );
      }}
    </SlideoutMenu>
  );
};

interface IBillSlideoutProps {
  billId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const BillSlideout = ({ billId, isOpen, onClose }: IBillSlideoutProps) => {
  const { user } = useAuthState();
  const navigate = useNavigateWithDateRange();
  const [selectedTransactionId, setSelectedTransactionId] = React.useState<string | null>(null);

  const [fetchBill, { data, error }] = useLazyQuery<GetBillDetailsQuery, GetBillDetailsQueryVariables>(
    GET_BILL_DETAILS
  );

  React.useEffect(() => {
    if (isOpen && billId) {
      void fetchBill({ variables: { id: billId } });
    }
  }, [isOpen, billId, fetchBill]);

  React.useEffect(() => {
    if (error) {
      onClose();
    }
  }, [error, onClose]);

  const bill = React.useMemo((): BillDetails | null => {
    if (!billId) {
      return null;
    }
    const fetched = data?.getBill;
    if (!fetched || fetched.id !== billId) {
      return null;
    }
    return fetched;
  }, [data?.getBill, billId]);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  const onClickMerchant = React.useCallback(() => {
    if (bill?.merchant?.id) {
      onClose();
      void navigate(PATHS.MERCHANT(bill.merchant.id));
    }
  }, [bill?.merchant?.id, navigate, onClose]);

  const onClickFinancialAccount = React.useCallback(() => {
    const account = bill?.transactions[0]?.financialAccount;
    if (account?.id) {
      onClose();
      void navigate(PATHS.ACCOUNT(account.id));
    }
  }, [bill?.transactions, navigate, onClose]);

  const handleNavigateToTransaction = React.useCallback(
    (transactionId: string) => {
      onClose();
      setSelectedTransactionId(transactionId);
    },
    [onClose]
  );

  return (
    <>
      <BaseBillSlideout
        bill={bill}
        isOpen={isOpen}
        isAdmin={user?.isAdmin ?? false}
        onOpenChange={handleOpenChange}
        onClickMerchant={bill?.merchant?.id ? onClickMerchant : undefined}
        onClickFinancialAccount={onClickFinancialAccount}
        onNavigateToTransaction={handleNavigateToTransaction}
      />
      <TransactionSlideout
        transactionId={selectedTransactionId}
        isOpen={selectedTransactionId !== null}
        onClose={() => setSelectedTransactionId(null)}
      />
    </>
  );
};

export default BillSlideout;
